import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getLocale, useIntl } from 'umi';
import Loading from '@/pages/dashboard/components/Loadings';
import { issueEmbedToken } from '@/pages/dashboard/services/dashboard';
import { ThemeContext } from '@/pages/home/wrappers/common';
import {
  SYSTEM_THEME_DARK,
  SYSTEM_THEME_LIGHT,
} from '@/pages/home/_config/config';
import type {
  IEmbedPreference,
  IPreferenceUpdateMessage,
  IRefreshRequestMessage,
  IRefreshResponseMessage,
  ISecureIframeRenderProps,
  TEmbedLanguage,
  TEmbedTheme,
  TRefreshDeniedReason,
} from './types';
import {
  applyEmbedPath,
  buildFallbackUrl,
  buildIframeUrl,
  getTokenPlacement,
  PREFERENCE_UPDATE_TYPE,
  readThemeFromStorage,
} from './utils';
import './index.scss';

const REFRESH_REQUEST_TYPE = 'databrain:refresh:request';
const REFRESH_RESPONSE_TYPE = 'databrain:refresh:response';

const normalizeLanguage = (locale: string): TEmbedLanguage =>
  locale === 'en-US' ? 'en-US' : 'zh-CN';

const normalizeTheme = (raw: string | undefined): TEmbedTheme =>
  raw === SYSTEM_THEME_DARK ? SYSTEM_THEME_DARK : SYSTEM_THEME_LIGHT;

/**
 * 把后端返回的错误对象映射到 reason；用于 postMessage 响应。
 */
const errorToReason = (err: any): TRefreshDeniedReason => {
  const code = err?.code;
  if (code === 20001 || err?.status === 401) return 'logged_out';
  if (code === 80003 || err?.status === 403) return 'no_permission';
  if (code === 80001 || code === 80002 || err?.status === 404) return 'not_found';
  return 'internal_error';
};

const syncExpectedOrigin = (
  rawUrl: string,
  originRef: React.MutableRefObject<string>,
): void => {
  try {
    originRef.current = new URL(rawUrl).origin;
  } catch {
    originRef.current = '';
  }
};

const SecureIframeRender: React.FC<ISecureIframeRenderProps> = ({
  resourceId,
  className,
  sandbox,
  fallbackUrl,
  embedPath,
  onRouteChange,
  iframeAllow,
}) => {
  // 触发 locale 切换后组件重渲染（与 NavHeader locale.tsx 一致）
  useIntl();
  const locale = getLocale();
  const themeContext = useContext(ThemeContext);
  const themeRaw = themeContext?.themeValue || readThemeFromStorage();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const expectedOriginRef = useRef<string>('');
  const seqRef = useRef(0);

  const [iframeUrl, setIframeUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const preference = useMemo<IEmbedPreference>(
    () => ({
      language: normalizeLanguage(locale),
      theme: normalizeTheme(themeRaw),
    }),
    [locale, themeRaw],
  );
  const preferenceRef = useRef(preference);
  preferenceRef.current = preference;

  const postPreferenceToIframe = useCallback((pref: IEmbedPreference) => {
    const origin = expectedOriginRef.current;
    const win = iframeRef.current?.contentWindow;
    if (!origin || !win) return;

    const payload: IPreferenceUpdateMessage = {
      type: PREFERENCE_UPDATE_TYPE,
      language: pref.language,
      theme: pref.theme,
    };
    win.postMessage(payload, origin);
  }, []);

  const handleIframeLoad = useCallback(() => {
    postPreferenceToIframe(preference);
  }, [preference, postPreferenceToIframe]);

  // ===== 签发主流程 =====
  const sign = useCallback(async () => {
    if (!resourceId) {
      setIframeUrl('');
      setLoading(false);
      return;
    }
    const mySeq = ++seqRef.current;
    setLoading(true);
    try {
      const { jwt, url } = await issueEmbedToken({ resourceId });
      if (mySeq !== seqRef.current) return;
      const pref = preferenceRef.current;
      const tokenPlacement = getTokenPlacement(url);
      // 有父地址栏保存的深链时，替换菜单 url 的路径，实现刷新恢复
      const targetUrl = embedPath ? applyEmbedPath(url, embedPath) : url;
      const full = buildIframeUrl(targetUrl, jwt, pref, tokenPlacement);
      syncExpectedOrigin(targetUrl, expectedOriginRef);
      setIframeUrl(full);
      setLoading(false);
    } catch {
      if (mySeq !== seqRef.current) return;
      const rawFallback = fallbackUrl?.trim() || '';
      const pref = preferenceRef.current;
      syncExpectedOrigin(rawFallback, expectedOriginRef);
      setIframeUrl(
        rawFallback ? buildFallbackUrl(rawFallback, pref) : '',
      );
      setLoading(false);
    }
  }, [resourceId, fallbackUrl, embedPath]);

  useEffect(() => {
    sign();
    return () => {
      seqRef.current += 1;
    };
  }, [sign]);

  // language / theme 变化时热更新（不 reload iframe）
  useEffect(() => {
    if (!iframeUrl || loading) return;
    postPreferenceToIframe(preference);
  }, [preference, iframeUrl, loading, postPreferenceToIframe]);

  // ===== postMessage：token 续签 =====
  useEffect(() => {
    const onMessage = async (e: MessageEvent) => {
      const expectedOrigin = expectedOriginRef.current;
      if (!expectedOrigin || e.origin !== expectedOrigin) return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      const data = e.data as IRefreshRequestMessage | undefined;
      if (!data || data.type !== REFRESH_REQUEST_TYPE || !data.requestId) {
        return;
      }
      if (!resourceId) return;

      const send = (resp: IRefreshResponseMessage) => {
        iframeRef.current?.contentWindow?.postMessage(resp, expectedOrigin);
      };

      try {
        const { jwt, url } = await issueEmbedToken({ resourceId });
        send({
          type: REFRESH_RESPONSE_TYPE,
          requestId: data.requestId,
          ok: true,
          token: jwt,
          url,
        });
      } catch (err: any) {
        send({
          type: REFRESH_RESPONSE_TYPE,
          requestId: data.requestId,
          ok: false,
          reason: errorToReason(err),
        });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [resourceId]);

  // ===== postMessage：子应用路由变化通知 =====
  useEffect(() => {
    if (!iframeUrl || !onRouteChange) return;
    const onMessage = (e: MessageEvent) => {
      const expectedOrigin = expectedOriginRef.current;
      if (!expectedOrigin || e.origin !== expectedOrigin) return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      const data = e.data as { type?: string; fullPath?: string } | undefined;
      if (data?.type === 'route-change' && typeof data.fullPath === 'string') {
        onRouteChange(data.fullPath);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [iframeUrl, onRouteChange]);

  if (!resourceId) return null;

  const wrapClass = `dbd-secure-iframe${className ? ` ${className}` : ''}`;

  return (
    <div className={wrapClass}>
      {iframeUrl ? (
        <iframe
          ref={iframeRef}
          key={iframeUrl}
          className='dbd-secure-iframe__iframe'
          src={iframeUrl}
          sandbox={sandbox}
          allow={iframeAllow}
          title='databrain-secure-embed'
          onLoad={handleIframeLoad}
        />
      ) : null}
      {loading && (
        <div className='dbd-secure-iframe__overlay'>
          <Loading />
        </div>
      )}
    </div>
  );
};

export default SecureIframeRender;
